/**
 * 主体（saliency）检测：人脸检测无结果时回退使用，输出与人脸同结构的归一化 bbox。
 *
 * - 模型：本地静态资源 U²-Net / U²-Netp（Apache 2.0），目录由 `FJConfig.SUBJECT_MODEL_URL` 指定
 * - ORT WASM：本地 `FJConfig.ORT_WASM_PATH`（见 scripts/copy-ort-wasm.mjs）
 * - 输出：`[{x, y, width, height, isSubject: true}]`，归一化 [0,1]，直接灌入 `facesData`。
 */

import FJConfig from '../../config/FJConfig.js';

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_DEVICE = 'webgpu';
const DEFAULT_THRESHOLD = 0.72;
const MAX_SUBJECT_WIDTH = 0.65;
const DEFAULT_INPUT_NAME = 'input';
/** 前景像素数下限：低于此值视为画面无明显主体（避免散点噪声） */
const MIN_FOREGROUND_PIXELS = 50;

// ─── Module-level singletons ──────────────────────────────────────────────────

let _model = null;
let _processor = null;
let _tjs = null;
let _loadPromise = null;
/** 模型输入张量名，从 `config.input_name` 读取，如 `input.1` */
let _modelInputName = DEFAULT_INPUT_NAME;
/** 当前模型加载使用的后端，fallback 后与调用方传入的 device 可能不同 */
let _currentDevice = null;
/**
 * 本会话内主体检测是否仍可用。模型加载/推理致命失败后置 false，dispose 后重置。
 * 调用方应回退为人脸检测结果，避免每帧重复尝试加载。
 */
let _available = true;

/** CPU-backed canvas：专用于高频 getImageData，避免 GPU readback 同步开销 */
let _readbackCanvas = null;
let _readbackCtx = null;

/**
 * 复用 scratch 内存（bin / labels / queue），避免每帧分配 ~2MB typed array 触发 GC。
 * 仅在尺寸扩大时重新分配。
 */
let _scratch = { size: 0, bin: null, labels: null, queue: null };

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * 判断 face-data 项是否为主体回退（而非真实人脸）。
 * @param {object} face
 * @returns {boolean}
 */
export function isSubjectFace(face) {
    return !!face && face.isSubject === true;
}

/**
 * 本会话内主体检测是否可用（加载失败后为 false，{@link disposeSubjectDetector} 后恢复）。
 * @returns {boolean}
 */
export function isSubjectDetectAvailable() {
    return _available;
}

/**
 * 返回当前检测器的运行时状态，便于调用方判断 device 实际生效值或做排障。
 * @returns {{ loaded: boolean, available: boolean, device: string|null, modelInputName: string }}
 */
export function getSubjectDetectorStatus() {
    return {
        loaded: _model !== null,
        available: _available,
        device: _currentDevice,
        modelInputName: _modelInputName,
    };
}

/**
 * 尝试预热主体模型；失败时不抛错，并标记本会话内不可用。
 * @param {object} opts  同 {@link ensureSubjectModelLoaded}
 * @returns {Promise<boolean>} 是否已成功加载且可用
 */
export async function tryEnsureSubjectModelLoaded(opts) {
    if (!isSubjectDetectAvailable()) return false;
    try {
        await ensureSubjectModelLoaded(opts);
        return _model !== null;
    } catch {
        return false;
    }
}

/**
 * 标记本会话内禁用主体检测并释放模型状态（后续 detect 直接返回空）。
 */
function disableSubjectDetect() {
    if (!_available) return;
    _available = false;
    resetModel();
}

/**
 * 懒加载本地 U²-Net 模型，幂等。失败时清空状态，下次调用会重试。
 *
 * @param {object} opts
 * @param {string} opts.modelUrl  模型目录 URL（必须以 `/` 结尾），如 `/public/models/u2net/`
 * @param {string} [opts.device='webgpu']
 * @returns {Promise<void>}
 */
export async function ensureSubjectModelLoaded({ modelUrl, device = DEFAULT_DEVICE } = {}) {
    if (!isSubjectDetectAvailable()) return;
    if (_model) return;
    if (_loadPromise) return _loadPromise;
    if (!modelUrl) throw new Error('[SubjectDetect] ensureSubjectModelLoaded: modelUrl 必填，格式如 /path/to/model/');

    const { localPath, modelDir } = splitModelUrl(modelUrl);
    _loadPromise = loadModelFromLocal(localPath, modelDir, device).catch(err => {
        disableSubjectDetect();
        throw err;
    });
    return _loadPromise;
}

/**
 * 卸载模型并释放资源。在 store dispose / 工程切换时调用，避免 ORT runtime 常驻内存。
 */
export function disposeSubjectDetector() {
    _available = true;
    resetModel();
    _readbackCanvas = null;
    _readbackCtx = null;
    _scratch = { size: 0, bin: null, labels: null, queue: null };
}

/**
 * 对 canvas 跑主体检测，返回归一化 bbox 数组（最多一个，无主体时返回空数组）。
 *
 * @param {HTMLCanvasElement} canvas
 * @param {object} opts
 * @param {string} opts.modelUrl  同 {@link ensureSubjectModelLoaded}
 * @param {string} [opts.device]
 * @param {number} [opts.threshold=0.65]  mask 二值化阈值（0~1）
 * @returns {Promise<Array<{x:number,y:number,width:number,height:number,isSubject:true}>>}
 */
export async function detectSubjectBBox(canvas, opts = {}) {
    const { bboxes } = await detectSubject(canvas, opts);
    return bboxes;
}

/**
 * 对 canvas 跑主体检测，返回 bbox 与显著图（灰度 mask，与 canvas 同尺寸）。
 *
 * @param {HTMLCanvasElement} canvas
 * @param {object} opts  同 {@link detectSubjectBBox}
 * @returns {Promise<{
 *   bboxes: Array<{x:number,y:number,width:number,height:number,isSubject:true}>,
 *   saliency: { data: Uint8ClampedArray, width: number, height: number } | null
 * }>}
 */
export async function detectSubject(canvas, opts = {}) {
    const empty = { bboxes: [], saliency: null };
    if (!isSubjectDetectAvailable()) return empty;
    if (!(canvas instanceof HTMLCanvasElement)) return empty;
    const w = canvas.width;
    const h = canvas.height;
    if (w <= 0 || h <= 0) return empty;

    try {
        await ensureSubjectModelLoaded(opts);
    } catch {
        return empty;
    }

    if (!_model) return empty;
    const maskU8 = await runInference(canvas, w, h);
    if (!maskU8) return empty;

    const threshold255 = Math.round((opts.threshold ?? DEFAULT_THRESHOLD) * 255);
    const bbox = extractLargestSubjectBBox(maskU8, w, h, threshold255);
    if (bbox && bbox.width > MAX_SUBJECT_WIDTH) {
        return empty;
    }
    return {
        bboxes: bbox ? [{ ...bbox, isSubject: true }] : [],
        saliency: { data: maskU8, width: w, height: h },
    };
}

// ─── Internals: model loading ────────────────────────────────────────────────

/**
 * 把完整 modelUrl 拆成 transformers.js 需要的 `localModelPath`（基础路径）+ `modelDir`（子目录名）。
 * 例：`/public/models/u2net/` → `{ localPath: '/public/models/', modelDir: 'u2net' }`
 */
function splitModelUrl(modelUrl) {
    const trimmed = modelUrl.replace(/\/+$/, '');
    const lastSlash = trimmed.lastIndexOf('/');
    if (lastSlash < 0) {
        throw new Error(`[SubjectDetect] 非法 modelUrl: "${modelUrl}"（期望格式：/path/to/modelDir/）`);
    }
    return {
        localPath: trimmed.slice(0, lastSlash + 1),
        modelDir: trimmed.slice(lastSlash + 1),
    };
}

const ORT_WASM_FILE = 'ort-wasm-simd-threaded.wasm';

/** 解析静态资源目录为绝对 URL（兼容 base: './' 与子路径部署） */
function resolveStaticAssetBase(basePath) {
    if (typeof window === 'undefined' || !basePath) return basePath;
    if (/^https?:\/\//i.test(basePath)) return basePath.endsWith('/') ? basePath : `${basePath}/`;
    const normalized = basePath.startsWith('/') ? basePath : `/${basePath}`;
    return new URL(normalized.endsWith('/') ? normalized : `${normalized}/`, window.location.origin).href;
}

/** 启动前校验 ORT WASM 可访问且为二进制（避免 404 返回 index.html 导致 magic word 错误） */
async function assertOrtWasmReachable(wasmBaseUrl) {
    const url = `${wasmBaseUrl}${ORT_WASM_FILE}`;
    const res = await fetch(url);
    if (!res.ok) {
        throw new Error(`[SubjectDetect] 无法加载 ORT WASM (${res.status}): ${url}`);
    }
    const buf = new Uint8Array(await res.arrayBuffer());
    const magic = buf[0] === 0x00 && buf[1] === 0x61 && buf[2] === 0x73 && buf[3] === 0x6d;
    if (!magic) {
        throw new Error(`[SubjectDetect] ORT WASM 响应非二进制（可能路径错误或返回了 HTML），请检查: ${url}`);
    }
}

/** 主线程单线程 WASM；wasmPaths 必须为目录 URL 或 { wasm, mjs }，不可用文件名 map */
async function configureOrtWasmEnv(tjs) {
    const wasmPaths = resolveStaticAssetBase(FJConfig.ORT_WASM_PATH);
    await assertOrtWasmReachable(wasmPaths);
    tjs.env.backends.onnx.wasm = Object.assign({}, tjs.env.backends.onnx.wasm || {}, {
        wasmPaths,
        simd: true,
        numThreads: 1,
        proxy: false,
    });
}

function resolveOrtDevice(tjs, device) {
    if (device !== 'webgpu') return device;
    const webgpuAvailable = tjs.apis?.IS_WEBGPU_AVAILABLE ?? !!globalThis.navigator?.gpu;
    if (webgpuAvailable) return 'webgpu';
    return 'wasm';
}

async function loadModelFromLocal(localPath, modelDir, device) {
    const tjs = await import('@huggingface/transformers');
    const ortDevice = resolveOrtDevice(tjs, device);

    tjs.env.allowLocalModels = true;
    tjs.env.allowRemoteModels = false;
    tjs.env.useBrowserCache = true;
    tjs.env.localModelPath = localPath;
    await configureOrtWasmEnv(tjs);

    const [model, processor] = await Promise.all([
        tjs.AutoModel.from_pretrained(modelDir, { device: ortDevice }),
        // U2NetImageProcessor 未合入 transformers.js；AutoImageProcessor 会回退到通用 ImageProcessor（config 含 size/pad_size 即可）
        tjs.AutoImageProcessor.from_pretrained(modelDir),
    ]);

    _tjs = tjs;
    _model = model;
    _processor = processor;
    _modelInputName = resolveModelInputName(model);
    _currentDevice = ortDevice;
}

/**
 * 清空模型相关状态，保留 readback canvas 和 scratch（与模型生命周期无关，
 * 由 disposeSubjectDetector 统一回收）。
 */
function resetModel() {
    _model = null;
    _processor = null;
    _tjs = null;
    _modelInputName = DEFAULT_INPUT_NAME;
    _currentDevice = null;
    _loadPromise = null;
}

/**
 * 获取（或按需创建/resize）专用 CPU-backed readback canvas。
 * `willReadFrequently: true` 告知浏览器用 CPU 内存后备，getImageData 为 memcpy 而非 GPU 同步。
 */
function ensureReadbackCanvas(w, h) {
    if (!_readbackCanvas) {
        _readbackCanvas = document.createElement('canvas');
        _readbackCtx = _readbackCanvas.getContext('2d', { willReadFrequently: true });
    }
    if (_readbackCanvas.width !== w || _readbackCanvas.height !== h) {
        _readbackCanvas.width = w;
        _readbackCanvas.height = h;
    }
    return _readbackCtx;
}

/**
 * 获取（或按需扩容）帧级复用 scratch buffer，避免每帧分配大块 typed array。
 * 使用前调用方需自行 fill(0)。
 */
function ensureScratch(total) {
    if (_scratch.size < total) {
        _scratch = {
            size: total,
            bin: new Uint8Array(total),
            labels: new Int32Array(total),
            queue: new Int32Array(total),
        };
    }
    return _scratch;
}

/**
 * 从模型 config 或 ORT session 解析输入张量名（如 `input.1`）。
 * 优先 config.input_name[0]，否则回退到 session.inputNames[0]，最终默认 `input`。
 */
function resolveModelInputName(model) {
    const raw = model?.config?.input_name;
    const cfgName = Array.isArray(raw) ? raw[0] : typeof raw === 'string' ? raw : null;
    if (typeof cfgName === 'string' && cfgName) return cfgName;
    const sessionInputs = model?.sessions?.model?.inputNames;
    if (Array.isArray(sessionInputs) && sessionInputs[0]) return sessionInputs[0];
    return DEFAULT_INPUT_NAME;
}

// ─── Internals: inference ────────────────────────────────────────────────────

/**
 * 运行一次推理：canvas → 灰度 mask（与 canvas 同尺寸）。
 * 失败时返回 null（调用方返回空 bbox 数组）。
 * @returns {Promise<Uint8ClampedArray | null>}
 */
async function runInference(canvas, w, h) {
    try {
        const rawImg = canvasToRawImage(canvas, w, h);
        if (!rawImg) return null;

        const inputs = await _processor(rawImg);
        const out = await _model({ [_modelInputName]: inputs.pixel_values });
        return await tensorToMaskData(out, w, h);
    } catch {
        return null;
    }
}

/**
 * 将 canvas 内容读出为 RawImage（CPU readback 路径）。
 * @returns {InstanceType<typeof _tjs.RawImage> | null}
 */
function canvasToRawImage(canvas, w, h) {
    const readCtx = ensureReadbackCanvas(w, h);
    if (!readCtx) return null;
    readCtx.drawImage(canvas, 0, 0, w, h);
    const imgData = readCtx.getImageData(0, 0, w, h);
    return new _tjs.RawImage(imgData.data, w, h, 4);
}

/**
 * 从已加载的 AutoProcessor 读取 letterbox 参数（与 preprocessor_config.json 一致）。
 * @returns {{ padW: number, padH: number, longestEdge: number }}
 */
function getProcessorLetterboxCfg() {
    const ip = _processor?.image_processor ?? _processor;
    const cfg = ip?.config ?? {};
    const padSize = cfg.pad_size ?? { width: 320, height: 320 };
    return {
        padW: padSize.width ?? 320,
        padH: padSize.height ?? 320,
        longestEdge: cfg.size?.longest_edge ?? cfg.size?.shortest_edge ?? 320,
    };
}

/**
 * 与 transformers.js `get_resize_output_image_size` 一致（仅 longest_edge、不放大）。
 * @returns {{ contentW: number, contentH: number }}
 */
function computeResizeOutputSize(srcW, srcH, longestEdge) {
    const longResizeFactor = longestEdge / Math.max(srcW, srcH);
    return {
        contentW: Math.floor(Number((srcW * longResizeFactor).toFixed(2))),
        contentH: Math.floor(Number((srcH * longResizeFactor).toFixed(2))),
    };
}

/**
 * 与 U²-Net 预处理对称：最长边缩到 longestEdge，再 pad 到 padW×padH。
 * transformers.js `pad_image` 默认 center=false，内容贴左上角。
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
function computeLetterboxContentRect(srcW, srcH, padW, padH, longestEdge) {
    const { contentW, contentH } = computeResizeOutputSize(srcW, srcH, longestEdge);
    return {
        x: 0,
        y: 0,
        width: Math.min(contentW, padW),
        height: Math.min(contentH, padH),
    };
}

/**
 * 将 pad 空间下的 crop 映射到模型实际输出的 mask 分辨率（二者不一致时按比例缩放）。
 */
function mapCropToMaskSpace(crop, padW, padH, maskW, maskH) {
    if (maskW === padW && maskH === padH) return crop;
    const scaleX = maskW / padW;
    const scaleY = maskH / padH;
    return {
        x: Math.round(crop.x * scaleX),
        y: Math.round(crop.y * scaleY),
        width: Math.round(crop.width * scaleX),
        height: Math.round(crop.height * scaleY),
    };
}

/**
 * 从正方形 mask 中 crop 掉 letterbox 补边，再双线性缩放到原图尺寸。
 * @param {Uint8ClampedArray} maskData
 * @returns {Uint8ClampedArray}
 */
function cropLetterboxAndResizeMask(maskData, maskW, maskH, crop, outW, outH) {
    const { x, y, width: cw, height: ch } = crop;
    const srcCanvas = document.createElement('canvas');
    srcCanvas.width = maskW;
    srcCanvas.height = maskH;
    const srcCtx = srcCanvas.getContext('2d', { willReadFrequently: true });
    if (!srcCtx) return maskData;

    const srcImg = srcCtx.createImageData(maskW, maskH);
    for (let i = 0; i < maskW * maskH; i++) {
        const v = maskData[i] ?? 0;
        const j = i * 4;
        srcImg.data[j] = v;
        srcImg.data[j + 1] = v;
        srcImg.data[j + 2] = v;
        srcImg.data[j + 3] = 255;
    }
    srcCtx.putImageData(srcImg, 0, 0);

    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = cw;
    cropCanvas.height = ch;
    const cropCtx = cropCanvas.getContext('2d');
    cropCtx?.drawImage(srcCanvas, x, y, cw, ch, 0, 0, cw, ch);

    const outCanvas = document.createElement('canvas');
    outCanvas.width = outW;
    outCanvas.height = outH;
    const outCtx = outCanvas.getContext('2d', { willReadFrequently: true });
    if (!outCtx) return maskData;
    outCtx.drawImage(cropCanvas, 0, 0, outW, outH);

    const outPixels = outCtx.getImageData(0, 0, outW, outH).data;
    const result = new Uint8ClampedArray(outW * outH);
    for (let i = 0; i < outW * outH; i++) {
        result[i] = outPixels[i * 4] ?? 0;
    }
    return result;
}

/**
 * 将模型输出张量后处理为与原始 canvas 同尺寸的灰度 mask（Uint8ClampedArray）。
 * 先去掉预处理 letterbox 补边，再缩放到原图尺寸，避免宽高比失真。
 * @returns {Promise<Uint8ClampedArray>}
 */
async function tensorToMaskData(modelOut, w, h) {
    let tensor = modelOut.output ?? modelOut.output_image ?? Object.values(modelOut)[0];
    if (!tensor) throw new Error('[SubjectDetect] 模型输出中找不到有效张量，请检查模型格式');
    while (tensor.dims.length > 3 && tensor.dims[0] === 1) {
        tensor = tensor.squeeze(0);
    }
    const maskRaw = _tjs.RawImage.fromTensor(tensor.mul(255).to('uint8'));
    const maskW = maskRaw.width;
    const maskH = maskRaw.height;

    const { padW, padH, longestEdge } = getProcessorLetterboxCfg();
    const cropInPad = computeLetterboxContentRect(w, h, padW, padH, longestEdge);
    const crop = mapCropToMaskSpace(cropInPad, padW, padH, maskW, maskH);

    return cropLetterboxAndResizeMask(maskRaw.data, maskW, maskH, crop, w, h);
}

// ─── Internals: BBox extraction ──────────────────────────────────────────────

/**
 * 阈值化 → 找最大 4 邻接连通域 → 归一化 [0,1]²。
 * @returns {{x:number,y:number,width:number,height:number} | null}
 */
function extractLargestSubjectBBox(maskU8, w, h, threshold255) {
    const total = w * h;
    if (total <= 0 || maskU8.length < total) return null;

    const fgCount = binarize(maskU8, total, threshold255);
    if (fgCount < MIN_FOREGROUND_PIXELS) return null;

    const { bin } = _scratch;
    const rect = findLargestConnectedComponent(bin, w, h);
    return rect ? normalizeRectPx(rect, w, h) : null;
}

/**
 * 对 scratch.bin 做原地阈值二值化，返回前景像素数。
 * 使用 ensureScratch 分配的复用 buffer，不额外分配内存。
 * @returns {number} fgCount
 */
function binarize(maskU8, total, threshold255) {
    const { bin } = ensureScratch(total);
    bin.fill(0);
    let fgCount = 0;
    for (let i = 0; i < total; i++) {
        if (maskU8[i] >= threshold255) {
            bin[i] = 1;
            fgCount++;
        }
    }
    return fgCount;
}

/**
 * 4 邻接 BFS：扫描全图找面积最大的连通域，返回其 AABB。
 * 性能：扁平 if + Int32Array 队列复用，640×360 全图 < 5ms。
 */
function findLargestConnectedComponent(bin, w, h) {
    const total = bin.length;
    const { labels, queue } = _scratch;
    labels.fill(0);

    let bestArea = 0;
    let bestRect = null;
    let label = 0;

    for (let i = 0; i < total; i++) {
        if (!bin[i] || labels[i]) continue;
        label++;

        let head = 0;
        let tail = 0;
        queue[tail++] = i;
        labels[i] = label;
        let minX = w;
        let minY = h;
        let maxX = -1;
        let maxY = -1;
        let area = 0;

        while (head < tail) {
            const idx = queue[head++];
            const x = idx % w;
            const y = (idx / w) | 0;
            area++;
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;

            if (x > 0 && bin[idx - 1] && !labels[idx - 1]) {
                labels[idx - 1] = label;
                queue[tail++] = idx - 1;
            }
            if (x + 1 < w && bin[idx + 1] && !labels[idx + 1]) {
                labels[idx + 1] = label;
                queue[tail++] = idx + 1;
            }
            if (y > 0 && bin[idx - w] && !labels[idx - w]) {
                labels[idx - w] = label;
                queue[tail++] = idx - w;
            }
            if (y + 1 < h && bin[idx + w] && !labels[idx + w]) {
                labels[idx + w] = label;
                queue[tail++] = idx + w;
            }
        }

        if (area > bestArea) {
            bestArea = area;
            bestRect = { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
        }
    }
    return bestRect;
}

function normalizeRectPx(rect, w, h) {
    return {
        x: rect.x / w,
        y: rect.y / h,
        width: rect.width / w,
        height: rect.height / h,
    };
}

